import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { I18nService } from '@app/i18n/services';
import { DialogService } from '@services/dialog';
import { FeedbackData, FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { ProgressIndicatorService } from '@services/progress';
import { TokenService } from '@services/token';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { forkJoin } from 'rxjs';

import { IAvailableRoles, UsersService } from '@services/users';
import { UsersRolesSourcesDialogComponent } from '../users-roles-sources-dialog';
import { IManageRolesDialogData } from './users-manage-roles.models';

@Component({
	selector: 'app-users-manage-roles',
	templateUrl: './users-manage-roles.dialog.html'
})
export class UsersManageRolesDialogComponent extends OnDestroyMixin implements OnInit {
	private dialogRefClose: () => void;
	private closeAfterSubmit = false;
	private i18nConfirmCognitoGroupsUpdate = this.i18nService.get('', 'app.prompt.users.update-cognito-groups.confirm-message');
	private i18nPendingChangesConfirmMessage = this.i18nService.get(
		'You have unapplied role changes that will be lost. Are you sure you want to close?',
		'app.prompt.pending-role-changes-confirm-message'
	);

	public formInputs = new FormGroup({
		roleToAdd: new FormControl(null)
	});

	public userRolesOriginal: string[];
	public userRoles: string[] = [];
	public availableRolesToAdd: string[] = [];
	public availableRolesToRemove: string[] = [];
	public filteredRolesToAdd: string[] = [];
	public showRoleSources = false;
	public canTroubleshootRoles = false;
	private readonly observerRole = 'ROLE_OBSERVER';

	@ViewChild(UsersRolesSourcesDialogComponent) private roleSourcesDialogComponent: UsersRolesSourcesDialogComponent;

	constructor(
		private i18nService: I18nService,
		private dialogService: DialogService,
		private feedbackService: FeedbackService,
		private usersService: UsersService,
		private progressIndicatorService: ProgressIndicatorService,
		private tokenService: TokenService,
		@Inject(MAT_DIALOG_DATA) public dialogData: IManageRolesDialogData,
		private dialogRef: MatDialogRef<UsersManageRolesDialogComponent>
	) {
		super();
		this.dialogRefClose = this.dialogService.interceptClose(this.dialogRef, this, this.closeDialog);
	}

	public ngOnInit(): void {
		this.setTroubleshootRolesVisibility();
		this.getUserRoles();
	}

	public removeRole(role: string): void {
		this.userRoles = this.userRoles.filter(r => r !== role);
		this.refreshDropDownList();
	}

	public addRole(role: string): void {
		if (!this.userRoles.includes(role)) {
			this.userRoles.push(role);
		}

		this.refreshDropDownList();
	}

	public toggleRoleSources(): void {
		if (!this.canTroubleshootRoles) {
			return;
		}

		this.showRoleSources = !this.showRoleSources;
	}

	private setTroubleshootRolesVisibility(): void {
		this.tokenService.getDecodedToken().subscribe({
			next: (decodedToken) => {
				this.canTroubleshootRoles = (decodedToken?.roles || []).includes(this.observerRole);
			},
			error: () => {
				this.canTroubleshootRoles = false;
			}
		});
	}

	private refreshDropDownList(): void {
		this.filteredRolesToAdd = this.availableRolesToAdd.filter(r => !this.userRoles.includes(r));
		this.formInputs.get('roleToAdd').reset();
	}

	public onFormSubmit(): void {
		if (!this.hasRoleChanges()) {
			if (this.closeAfterSubmit) {
				this.closeAfterSubmit = false;
				this.dialogRefClose();
			}
			return;
		}

		const changesMessage = this.formatRoleChangesMessage();
		const data: FeedbackData = {
			title: FeedbackTitle.warning,
			message: this.i18nConfirmCognitoGroupsUpdate
				? `${this.i18nConfirmCognitoGroupsUpdate} ${changesMessage}`
				: changesMessage,
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.updateRoles(this.closeAfterSubmit);
				} else {
					this.closeAfterSubmit = false;
				}
			}
		};

		this.feedbackService.confirm(data);
	}

	public setCloseAfterSubmit(closeAfterSubmit: boolean): void {
		this.closeAfterSubmit = closeAfterSubmit;
	}

	public closeDialog(): void {
		if (!this.hasRoleChanges()) {
			this.dialogRefClose();
			return;
		}

		this.feedbackService.confirm({
			title: FeedbackTitle.warning,
			message: `${this.i18nPendingChangesConfirmMessage} ${this.formatRoleChangesMessage()}`,
			style: FeedbackStyle.error,
			okButton: this.i18nService.get('Close', 'app.button.close'),
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.dialogRefClose();
				}
			}
		});
	}

	private hasRoleChanges(): boolean {
		return this.getRolesToAdd().length > 0 || this.getRolesToRemove().length > 0;
	}

	private getRolesToAdd(): string[] {
		return this.sortRoles(this.userRoles.filter(r => !this.userRolesOriginal.includes(r)));
	}

	private getRolesToRemove(): string[] {
		return this.sortRoles(this.userRolesOriginal.filter(r => !this.userRoles.includes(r)));
	}

	private formatRoleChangesMessage(): string {
		const rolesToAdd = this.getRolesToAdd();
		const rolesToRemove = this.getRolesToRemove();
		const parts: string[] = [];

		if (rolesToAdd.length > 0) {
			parts.push(`Add: ${rolesToAdd.join(', ')}`);
		}

		if (rolesToRemove.length > 0) {
			parts.push(`Remove: ${rolesToRemove.join(', ')}`);
		}

		return `Changes: ${parts.join(' | ')}`;
	}

	private updateRoles(closeAfterUpdate = false): void {
		const rolesToAdd = this.getRolesToAdd();
		const rolesToRemove = this.getRolesToRemove();

		const requests = [
			...rolesToAdd.map(role => this.usersService.addUserRole(this.dialogData.user.id, this.dialogData.user.email, role)),
			...rolesToRemove.map(role => this.usersService.removeUserRole(this.dialogData.user.id, this.dialogData.user.email, role))
		];

		// handle no change case - shouldn't happen since we check before calling this method
		if (requests.length === 0) {
			return;
		}

		this.progressIndicatorService.spinner(true);

		forkJoin(requests).subscribe({
			next: () => {
				// all requests are complete
				this.userRolesOriginal = this.sortRoles(this.userRoles);
				this.refreshRoleSourcesIfVisible();
				this.closeAfterSubmit = false;

				if (closeAfterUpdate) {
					this.dialogRefClose();
				}
			},
			error: (error: any) => {
				this.feedbackService.apiError(error);
				this.progressIndicatorService.spinner(false);
				this.closeAfterSubmit = false;
				this.dialogRef.close();
			}
		});
	}

	private refreshRoleSourcesIfVisible(): void {
		if (this.showRoleSources && this.roleSourcesDialogComponent) {
			this.roleSourcesDialogComponent?.getRolesSources();
		} else {
			this.progressIndicatorService.spinner(false);
		}
	}

	private initializeAvailableRoles(): void {
		const roles: IAvailableRoles = this.dialogData.availableRoles || { canAdd: [], canRemove: [] };
		this.availableRolesToAdd = this.sortRoles(roles.canAdd || []);
		this.availableRolesToRemove = this.sortRoles(roles.canRemove || []);
	}

	private getUserRoles(): void {
		this.progressIndicatorService.spinner(true);

		this.usersService
		 	.getUserRoles(this.dialogData.user.id, this.dialogData.user.email)
		 	.subscribe(
				(res: string[]) => {
		 			this.userRoles = this.sortRoles(res); // copy of list
		 			this.userRolesOriginal = this.sortRoles(res); // copy of list so changes to userRoles will not change this copy
		 			this.initializeAvailableRoles();
		 			this.refreshDropDownList();
		 			this.progressIndicatorService.spinner(false);
		 		},
		 		(error: any) => {
		 			this.feedbackService.apiError(error);
		 			this.progressIndicatorService.spinner(false);
		 			this.dialogRef.close();
		 		}
		 	);
	}

	private readonly alphabeticSort = (a: string, b: string): number => a.localeCompare(b, undefined, { sensitivity: 'base' });

	private sortRoles(roles: string[] = []): string[] {
		return [...roles].sort(this.alphabeticSort);
	}
}
